import { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Button, CircularProgress, Alert, Tooltip,
  TableContainer, TableSortLabel, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/userContext';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { listDesignRequests, markDesignDone, cancelDesignRequest, uploadDesignDrawings } from '../../api/designRequestApi';

const PP = "'Poppins', sans-serif";

const apiBase = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

function formatDateTime(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status }) {
  const isDone = status === 'Done';
  const isCancelled = status === 'Cancelled';
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontFamily: PP,
        fontSize: '0.73rem',
        fontWeight: 600,
        bgcolor: isDone ? '#DCFCE7' : isCancelled ? '#F1F5F9' : '#FEF9C3',
        color: isDone ? '#15803D' : isCancelled ? '#64748B' : '#92400E',
        border: 'none',
      }}
    />
  );
}

function TypeChips({ googleMap, droneShot }) {
  if (!googleMap && !droneShot) {
    return <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8' }}>—</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {googleMap && (
        <Chip
          label="Google Map"
          size="small"
          sx={{ fontFamily: PP, fontSize: '0.7rem', bgcolor: '#DBEAFE', color: '#1D4ED8' }}
        />
      )}
      {droneShot && (
        <Chip
          label="Drone Shot"
          size="small"
          sx={{ fontFamily: PP, fontSize: '0.7rem', bgcolor: '#F3E8FF', color: '#7C3AED' }}
        />
      )}
    </Box>
  );
}

function ImageLinks({ images, urls }) {
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Build a normalised list: prefer structured images[], fall back to urls[]
  const items = (images && images.length > 0)
    ? images
    : (urls && urls.length > 0)
      ? urls.map((url, i) => ({ url, fileName: `Image ${i + 1}` }))
      : [];

  if (items.length === 0) {
    return <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8' }}>—</Typography>;
  }

  const handleCopy = (url, i) => {
    const fullUrl = url.startsWith('http') ? url : `${apiBase}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {items.map((item, i) => {
        const fullUrl = item.url.startsWith('http') ? item.url : `${apiBase}${item.url}`;
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: PP, fontSize: '0.78rem', color: '#1D4ED8',
                textDecoration: 'none',
              }}
            >
              {item.fileName || `Image ${i + 1}`}
            </a>
            <Tooltip title={copiedIdx === i ? 'Copied!' : 'Copy link'}>
              <IconButton
                size="small"
                onClick={() => handleCopy(item.url, i)}
                sx={{ p: 0.3, color: copiedIdx === i ? '#15803D' : '#94A3B8' }}
              >
                <ContentCopyIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}

// ── sorting helpers ────────────────────────────────────────────────────────────

function getComparator(order, orderBy) {
  return (a, b) => {
    let av, bv;
    if (orderBy === 'designRequestId') {
      av = a.designRequestId; bv = b.designRequestId;
    } else if (orderBy === 'customerName') {
      av = (a.customerName || '').toLowerCase(); bv = (b.customerName || '').toLowerCase();
    } else if (orderBy === 'kwpPanels') {
      // Sort by numeric panels if available, else parse kWp string
      av = a.panels ?? parseFloat(a.kwpPanels) ?? 0;
      bv = b.panels ?? parseFloat(b.kwpPanels) ?? 0;
    } else if (orderBy === 'status') {
      av = a.status || ''; bv = b.status || '';
    } else if (orderBy === 'createdOn') {
      av = new Date(a.createdOn).getTime(); bv = new Date(b.createdOn).getTime();
    } else if (orderBy === 'doneOn') {
      av = a.doneOn ? new Date(a.doneOn).getTime() : 0;
      bv = b.doneOn ? new Date(b.doneOn).getTime() : 0;
    } else {
      av = a[orderBy]; bv = b[orderBy];
    }
    if (av < bv) return order === 'asc' ? -1 : 1;
    if (av > bv) return order === 'asc' ? 1 : -1;
    return 0;
  };
}

export default function DesignRequestList() {
  const navigate = useNavigate();
  const { user } = useUser();

  // Can mark done: user has DesignRequestAccess right (designers/admins)
  const canMarkDone = user?.roleRights?.includes('DesignRequestAccess');
  // Can submit a new request: only sales agents (CreateSale) — designers only fulfil.
  const canSubmit = user?.roleRights?.includes('CreateSale');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingId, setMarkingId] = useState(null);
  const [markError, setMarkError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  // Designer uploads finished work (gallery, ≤5 MB each) → DesignRequestImage Kind="drawing".
  const handleUploadDrawings = async (id, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const tooBig = files.find(f => f.size > 5 * 1024 * 1024);
    if (tooBig) { window.alert(`"${tooBig.name}" is over 5 MB — each file must be 5 MB or smaller.`); return; }
    setUploadingId(id);
    try {
      const images = await Promise.all(files.map(f => new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res({ dataBase64: String(r.result).split(',')[1], fileName: f.name });
        r.onerror = rej;
        r.readAsDataURL(f);
      })));
      await uploadDesignDrawings(id, images);
      loadData();
    } catch (err) {
      window.alert('Upload failed: ' + (err?.response?.data?.message || err?.response?.data || err?.message || 'unknown error'));
    } finally {
      setUploadingId(null);
    }
  };

  // Sort state — default: Created Date descending
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdOn');

  const loadData = useCallback(() => {
    setLoading(true);
    setError('');
    listDesignRequests()
      .then(res => setRows(res.data || []))
      .catch(() => setError('Failed to load design requests.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(col);
      setOrder('asc');
    }
  };

  const sortedRows = [...rows].sort(getComparator(order, orderBy));

  const handleMarkDone = async (id) => {
    setMarkingId(id);
    setMarkError('');
    try {
      await markDesignDone(id);
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to mark as done.';
      setMarkError(typeof msg === 'string' ? msg : 'Failed to mark as done.');
    } finally {
      setMarkingId(null);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this drawing request?')) return;
    setCancellingId(id);
    setMarkError('');
    try {
      await cancelDesignRequest(id);
      loadData();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to cancel request.';
      setMarkError(typeof msg === 'string' ? msg : 'Failed to cancel request.');
    } finally {
      setCancellingId(null);
    }
  };

  const cellSx = { fontFamily: PP, fontSize: '0.82rem', py: 1 };
  const headSx = { fontFamily: PP, fontSize: '0.75rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', py: 1.5, bgcolor: '#F8FAFC' };
  // The Action column is the last of 14 columns — pin it to the right edge so the
  // Mark Done / Edit / Upload buttons are always reachable without scrolling the
  // wide table all the way across (designers were missing them). (user 2026-06-25)
  const stickyHeadSx = { ...headSx, position: 'sticky', right: 0, zIndex: 3, borderLeft: '1px solid #E2E8F0', boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.12)' };
  const stickyCellSx = { ...cellSx, position: 'sticky', right: 0, zIndex: 1, bgcolor: '#fff', borderLeft: '1px solid #E2E8F0', boxShadow: '-6px 0 8px -6px rgba(0,0,0,0.12)' };

  const SortHead = ({ col, children }) => (
    <TableCell sx={{ ...headSx, cursor: 'pointer', userSelect: 'none' }}>
      <TableSortLabel
        active={orderBy === col}
        direction={orderBy === col ? order : 'asc'}
        onClick={() => handleSort(col)}
        sx={{
          fontFamily: PP, fontSize: '0.75rem', fontWeight: 700, color: '#64748B',
          textTransform: 'uppercase', letterSpacing: '0.4px',
          '&.Mui-active': { color: '#0C1B3A' },
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  // Base 11 columns (added Done Date + Designed By); +1 if Action column is visible
  const colSpan = 13 + ((canMarkDone || canSubmit) ? 1 : 0);

  return (
    <Box sx={{ px: 2, py: 2, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem', color: '#0C1B3A' }}>
          Design Requests
        </Typography>
        {canSubmit && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/design-requests/new')}
            sx={{
              fontFamily: PP, fontSize: '0.83rem', textTransform: 'none',
              bgcolor: '#0C1B3A', '&:hover': { bgcolor: '#1a2e5c' },
            }}
          >
            New Request
          </Button>
        )}
      </Box>

      {markError && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: PP, fontSize: '0.83rem' }} onClose={() => setMarkError('')}>
          {markError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ fontFamily: PP }}>{error}</Alert>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden', width: '100%' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortHead col="designRequestId">Design No.</SortHead>
                  <TableCell sx={headSx}>Agent</TableCell>
                  <SortHead col="customerName">Customer</SortHead>
                  <SortHead col="kwpPanels">Panels</SortHead>
                  <TableCell sx={headSx}>Type</TableCell>
                  <TableCell sx={headSx}>Remarks</TableCell>
                  <TableCell sx={headSx}>Map Link</TableCell>
                  <SortHead col="createdOn">Created Date</SortHead>
                  <SortHead col="status">Status</SortHead>
                  <TableCell sx={headSx}>Images</TableCell>
                  <TableCell sx={headSx}>Designed Drawing</TableCell>
                  <SortHead col="doneOn">Done Date</SortHead>
                  <TableCell sx={headSx}>Designed By</TableCell>
                  {(canMarkDone || canSubmit) && <TableCell sx={stickyHeadSx}>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} sx={{ textAlign: 'center', py: 5, fontFamily: PP, fontSize: '0.85rem', color: '#94A3B8' }}>
                      No design requests yet.
                    </TableCell>
                  </TableRow>
                ) : sortedRows.map((row) => (
                  <TableRow key={row.designRequestId} hover>
                    <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#0C1B3A' }}>
                      D{row.designRequestId}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>{row.agentName}</Typography>
                        {row.agentPhone && (
                          <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B' }}>{row.agentPhone}</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>{row.customerName}</TableCell>
                    <TableCell sx={cellSx}>
                      {row.panels != null ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                          <Box sx={{ fontWeight: 700, color: '#0C1B3A' }}>{row.panels} pcs</Box>
                          <Box sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{row.kwpPanels || '—'}</Box>
                        </Box>
                      ) : (row.kwpPanels || '—')}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TypeChips googleMap={row.designGoogleMap} droneShot={row.designDroneShot} />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 240, whiteSpace: 'normal', color: '#475569' }}>
                      {row.remarks || '—'}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {row.mapLink
                        ? <a href={row.mapLink} target="_blank" rel="noopener noreferrer" style={{ color: '#1A8CFF', fontFamily: PP, fontSize: '0.78rem', fontWeight: 600 }}>Open map</a>
                        : <Typography sx={{ fontFamily: PP, fontSize: '0.78rem', color: '#94A3B8' }}>—</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontSize: '0.78rem', color: '#64748B' }}>
                      {formatDateTime(row.createdOn)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <StatusBadge status={row.status} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ImageLinks images={row.images} urls={row.imageUrls} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        <ImageLinks images={row.drawings} urls={(row.drawings || []).map(d => d.url)} />
                        {canMarkDone && (
                          <Button component="label" size="small" variant="outlined"
                            disabled={uploadingId === row.designRequestId}
                            startIcon={uploadingId === row.designRequestId ? <CircularProgress size={12} color="inherit" /> : <UploadFileIcon sx={{ fontSize: 15 }} />}
                            sx={{ fontFamily: PP, fontSize: '0.72rem', textTransform: 'none', color: '#1A8CFF', borderColor: '#BFDBFE', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#EFF6FF', borderColor: '#93C5FD' } }}>
                            Upload
                            <input type="file" hidden multiple accept="image/*"
                              onChange={(e) => { handleUploadDrawings(row.designRequestId, e.target.files); e.target.value = ''; }} />
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontSize: '0.78rem', color: '#64748B' }}>
                      {formatDateTime(row.doneOn)}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#0C1B3A', fontWeight: 600 }}>
                      {row.status === 'Done' ? (row.doneByName || row.doneBy || '—') : '—'}
                    </TableCell>
                    {(canMarkDone || canSubmit) && (
                      <TableCell sx={stickyCellSx}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          {canMarkDone && row.status === 'Request' && (
                            <Tooltip title="Mark as Done">
                              <span>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={
                                    markingId === row.designRequestId
                                      ? <CircularProgress size={12} color="inherit" />
                                      : <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                                  }
                                  disabled={markingId === row.designRequestId}
                                  onClick={() => handleMarkDone(row.designRequestId)}
                                  sx={{
                                    fontFamily: PP, fontSize: '0.75rem', textTransform: 'none',
                                    color: '#15803D', borderColor: '#86EFAC',
                                    '&:hover': { bgcolor: '#F0FDF4', borderColor: '#4ADE80' },
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Mark Done
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {(canMarkDone || canSubmit) && row.status === 'Request' && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/design-requests/${row.designRequestId}/edit`)}
                              sx={{
                                fontFamily: PP, fontSize: '0.75rem', textTransform: 'none',
                                color: '#1D4ED8', borderColor: '#BFDBFE',
                                '&:hover': { bgcolor: '#EFF6FF', borderColor: '#93C5FD' },
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {(canMarkDone || canSubmit) && row.status === 'Request' && (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={cancellingId === row.designRequestId}
                              onClick={() => handleCancel(row.designRequestId)}
                              sx={{
                                fontFamily: PP, fontSize: '0.75rem', textTransform: 'none',
                                color: '#DC2626', borderColor: '#FECACA',
                                '&:hover': { bgcolor: '#FEF2F2', borderColor: '#F87171' },
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {cancellingId === row.designRequestId
                                ? <CircularProgress size={12} color="inherit" />
                                : 'Cancel'}
                            </Button>
                          )}
                          {row.status !== 'Request' && (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8' }}>
                              —
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
